/**
 * Docked desktop and full-width mobile patient notes panel.
 *
 * Notes are patient-wide; an optional source document is attached only when a
 * note is created from the currently displayed report.
 */

import { useAuth } from "@risalabs_frontend_org/auth-wrapper";
import { Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { usePatientNotes } from "../../../hooks/usePatientNotes";
import { NoteCard } from "./NoteCard";
import { NoteComposer } from "./NoteComposer";

interface PatientNotesPanelProps {
  mrn: string;
  patientName: string;
  sourceDocumentId?: string;
  targetNoteId?: string;
  onClose: () => void;
}

/**
 * Render patient-wide notes and orchestrate deep-link expansion/read behavior.
 *
 * @param props - Patient/source identity, target note, and close callback
 * @returns Responsive notes panel
 */
export function PatientNotesPanel({
  mrn,
  patientName,
  sourceDocumentId,
  targetNoteId,
  onClose,
}: PatientNotesPanelProps) {
  const { user } = useAuth();
  const controller = usePatientNotes(mrn, targetNoteId);
  const [expandedId, setExpandedId] = useState<string | null>(
    targetNoteId ?? null,
  );
  const [isComposing, setIsComposing] = useState(false);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const readDeepLinkRef = useRef<string | null>(null);

  useEffect(() => {
    if (!targetNoteId || controller.isLoading) return;
    setExpandedId(targetNoteId);
    const targetNote = controller.notes.find(
      (note) => note.note_id === targetNoteId,
    );
    if (
      readDeepLinkRef.current !== targetNoteId &&
      targetNote?.is_unread &&
      targetNote.assignee?.user_id === user?.id
    ) {
      readDeepLinkRef.current = targetNoteId;
      void controller.command(targetNoteId, "read");
    }
    const frame = window.requestAnimationFrame(() => {
      cardRefs.current[targetNoteId]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    controller.command,
    controller.isLoading,
    controller.notes,
    targetNoteId,
    user?.id,
  ]);

  /**
   * Expand a note and apply the shared-read rule only for its current assignee.
   *
   * @param noteId - Selected note identity
   * @returns Nothing
   */
  const toggleNote = (noteId: string) => {
    const nextId = expandedId === noteId ? null : noteId;
    setExpandedId(nextId);
    const note = controller.notes.find((item) => item.note_id === noteId);
    if (nextId && note?.is_unread && note.assignee?.user_id === user?.id) {
      void controller.command(noteId, "read");
    }
  };

  return (
    <aside
      aria-label="Patient notes"
      className="fixed inset-0 z-50 flex w-full flex-col border-l border-uiGray-200 bg-uiGray-50 shadow-xl md:static md:z-auto md:h-full md:w-[360px] md:shrink-0 md:shadow-none"
    >
      <header className="flex items-center justify-between border-b bg-white px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold">Patient Notes</h2>
            <span className="rounded-full bg-statusBlue-text px-2 py-0.5 text-xs font-bold text-white">
              {controller.notes.length}
            </span>
          </div>
          <p className="mt-0.5 max-w-[210px] truncate text-xs text-primaryGray-8">
            {patientName} · {mrn}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setIsComposing(true)}
            className="btn btn-outline gap-1 px-2 py-1 text-xs"
          >
            <Plus size={13} /> Add
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close patient notes"
            className="rounded p-1 hover:bg-uiGray-100"
          >
            <X size={17} />
          </button>
        </div>
      </header>
      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        {controller.error && (
          <div
            role="alert"
            className="rounded border border-red-200 bg-statusRed-bg p-3 text-xs text-statusRed-text"
          >
            {controller.error}{" "}
            <button
              type="button"
              onClick={controller.reload}
              className="font-semibold underline"
            >
              Retry
            </button>
          </div>
        )}
        {isComposing && (
          <NoteComposer
            assignees={controller.assignees}
            sourceDocumentId={sourceDocumentId}
            isSaving={controller.isSaving}
            onCancel={() => setIsComposing(false)}
            onSave={async (request) => {
              await controller.create(request);
              setIsComposing(false);
            }}
          />
        )}
        {controller.isLoading ? (
          <p className="py-10 text-center text-xs text-primaryGray-8">
            Loading notes…
          </p>
        ) : !controller.notes.length && !isComposing ? (
          <div className="py-10 text-center text-xs text-primaryGray-8">
            <p>No patient notes yet.</p>
            <button
              type="button"
              onClick={() => setIsComposing(true)}
              className="mt-2 font-semibold text-statusBlue-darkText"
            >
              Add the first note
            </button>
          </div>
        ) : (
          controller.notes.map((note) => (
            <div
              key={note.note_id}
              ref={(element) => {
                cardRefs.current[note.note_id] = element;
              }}
            >
              <NoteCard
                note={note}
                assignees={controller.assignees}
                isExpanded={expandedId === note.note_id}
                isSaving={controller.isSaving}
                onToggle={() => toggleNote(note.note_id)}
                onUpdate={(request) => controller.update(note.note_id, request)}
                onDelete={() => controller.remove(note.note_id)}
                onReply={(body) => controller.reply(note.note_id, { body })}
                onUpdateReply={(activityId, body) =>
                  controller.updateReply(note.note_id, activityId, { body })
                }
                onDeleteReply={(activityId) =>
                  controller.removeReply(note.note_id, activityId)
                }
                onCommand={(command) =>
                  controller.command(note.note_id, command)
                }
              />
            </div>
          ))
        )}
      </div>
    </aside>
  );
}
